import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Clock, Star, MapPin, Plane, Palmtree, Briefcase, CalendarDays, Users, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services, testimonials, vehicle } from '../data/mock';
import StarRating from '../components/StarRating';
import ReviewForm from '../components/ReviewForm';
import SEO from '../components/SEO';

const Home = () => {
  const [allReviews, setAllReviews] = useState(testimonials);
  
  const iconComponents = {
    'plane': Plane,
    'palm-tree': Palmtree,
    'briefcase': Briefcase,
    'calendar': CalendarDays
  };

  const handleNewReview = (review) => {
    setAllReviews(prev => [review, ...prev]);
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Home - Best Taxi Service in Nassau, Bahamas"
        description="RELAX & RIDE offers 24/7 taxi service in Nassau, Bahamas. Airport transfers, island tours, corporate transportation. 7-seater Honda Step Wagon. Official rates. Book now +1242-525-8675"
        keywords="Nassau taxi, Bahamas taxi service, airport transfer Nassau, Paradise Island taxi, taxi Bahamas, Nassau transportation, Atlantis taxi, best taxi Nassau"
      />
      {/* Hero Section */}
      <section 
        className="relative h-[600px] flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1562036861-44811e0b4481?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxiYWhhbWFzJTIwYmVhY2h8ZW58MHx8fHwxNzc0Mzk4ODIzfDA&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in">
            Your Journey Starts Here
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-gray-200">
            Premium taxi service in The Bahamas. Reliable, comfortable, and professional transportation 24/7.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking">
              <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg">
                Book Your Ride <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/services">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-black px-8 py-6 text-lg">
                Our Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4">Why Choose RELAX & RIDE?</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Experience the difference with our premium transportation service
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center hover:shadow-xl transition-shadow border-red-100">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Safe & Reliable</h3>
                <p className="text-gray-600">
                  Professional drivers with excellent safety records and local knowledge
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-shadow border-red-100">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">24/7 Available</h3>
                <p className="text-gray-600">
                  Round-the-clock service for all your transportation needs, any day
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-shadow border-red-100">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Top Rated</h3>
                <p className="text-gray-600">
                  Highly rated by tourists and locals for exceptional service quality
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-shadow border-red-100">
              <CardContent className="pt-8 pb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Local Experts</h3>
                <p className="text-gray-600">
                  Extensive knowledge of Nassau and Paradise Island destinations
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4">Our Services</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Comprehensive transportation solutions for every need
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => {
              const IconComponent = iconComponents[service.icon];
              return (
                <Card key={service.id} className="hover:shadow-xl transition-all border-t-4 border-red-600 group">
                  <CardContent className="pt-6">
                    <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mb-4">
                      <IconComponent className="w-7 h-7 text-red-600" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3 group-hover:text-red-600 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                    <Link to="/services" className="text-red-600 font-medium text-sm hover:underline inline-flex items-center">
                      Learn More <ArrowRight className="ml-1 w-4 h-4" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-3">
                View All Services
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Our Vehicle Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-600/10 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Vehicle</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Ride in comfort and style with our spacious 7-seater minivan
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Vehicle Image with Silhouette Effect */}
            <div className="relative">
              <div className="absolute inset-0 bg-red-600/20 rounded-2xl blur-2xl"></div>
              <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-red-600/30">
                <img 
                  src="https://images.unsplash.com/photo-1675311183084-755007dbb223?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxtaW5pdmFufGVufDB8fHx8MTc3NDM5OTczN3ww&ixlib=rb-4.1.0&q=85"
                  alt="Honda Step Wagon - 7 Seater"
                  className="w-full h-auto rounded-lg shadow-2xl"
                  style={{ filter: 'brightness(0.9) contrast(1.1)' }}
                />
                <div className="absolute bottom-12 left-12 bg-red-600 text-white px-6 py-3 rounded-lg shadow-xl">
                  <div className="flex items-center gap-2">
                    <Users className="w-6 h-6" />
                    <span className="text-2xl font-bold">7 Seats</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Vehicle Details */}
            <div>
              <h3 className="text-3xl font-bold mb-2 text-red-500">{vehicle.name}</h3>
              <p className="text-xl text-gray-400 mb-6">{vehicle.type}</p>
              <p className="text-gray-300 mb-8 leading-relaxed">
                {vehicle.description}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {vehicle.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-5 h-5 text-red-500" />
                    </div>
                    <span className="text-gray-200">{feature}</span>
                  </div>
                ))}
              </div>

              <div className="bg-red-600/10 border border-red-600/30 rounded-lg p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold mb-2">Perfect for Groups & Families</h4>
                    <p className="text-gray-300 text-sm">
                      With seating for up to 7 passengers and generous luggage space, our Honda Step Wagon 
                      is ideal for airport transfers, island tours, and family outings.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <Link to="/booking">
                  <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 text-lg">
                    Book This Vehicle <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4">What Our Customers Say</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Don't just take our word for it - hear from our satisfied customers
          </p>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
            {allReviews.slice(0, 6).map((testimonial) => (
              <Card key={testimonial.id} className="hover:shadow-xl transition-shadow">
                <CardContent className="pt-6">
                  <StarRating rating={testimonial.rating} size="default" />
                  <p className="text-gray-700 my-4 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-semibold">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.location}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Review Form */}
          <div className="max-w-3xl mx-auto">
            <ReviewForm onReviewSubmit={handleNewReview} />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="relative py-20 overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1544945582-052b29cd29e4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHx0cm9waWNhbCUyMHBhcmFkaXNlfGVufDB8fHx8MTc3NDM5ODgyN3ww&ixlib=rb-4.1.0&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Ride?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Book your taxi now and experience the best transportation service in The Bahamas
          </p>
          <Link to="/booking">
            <Button className="bg-red-600 hover:bg-red-700 text-white px-10 py-6 text-lg">
              Book Now <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
